import React from 'react';
import { MenuItem, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { dateConverter } from '../../../utils/methods';
import CommonText from '../../../form-components/CommonText';
import {
  FilterSectionFlex,
  SectionTitleText,
  SectionTitleWithCheckbox,
  FilterChip,
  SmallCheckbox,
  RadioContainer,
  RadioItem,
  StyledRadio,
  StyledSelect,
  CustomInputWrapper,
  CustomInput,
  CustomInputSuffix,
} from './MemberFilterStyled';

/**
 * "기간 + N회 이상/이하" 형태의 횟수 필터 한 섹션. 누적 방문수·수업 출석·수업 결석이 같은 모양이라 공용이다.
 * query 키는 `${prefix}Mode | Year | Month | RecentDays | Count | CountCondition | DateFrom | DateTo`.
 * 백엔드가 읽는 건 DateFrom/To·Count·CountCondition 뿐이고 Mode/Year/Month/RecentDays 는 화면 복원용이다.
 * 기간 모드는 전체기간(DateFrom/To 없음) / 년월 / 최근 N일 3종.
 */
export const COUNT_MODE_OPTIONS = [
  { value: 'ALL', label: '전체기간' },
  { value: 'MONTH', label: '년월' },
  { value: 'RECENT', label: '최근' },
];
export const COUNT_RECENT_PRESETS = [7, 14, 30];
const COUNT_CONDITION_OPTIONS = [
  { value: 'LTE', label: '이하' },
  { value: 'GTE', label: '이상' },
];
const YEAR_RANGE = 4; // 올해 포함 최근 4개년

const normalizeCountInput = value => value.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');

const computeDates = (prefix, mode, year, month, recentDays) => {
  if (mode === 'MONTH' && year && month) {
    const start = new Date(Number(year), Number(month) - 1, 1);
    const end = new Date(Number(year), Number(month), 0);
    return { [`${prefix}DateFrom`]: dateConverter(start), [`${prefix}DateTo`]: dateConverter(end) };
  }
  if (mode === 'RECENT' && recentDays) {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (Number(recentDays) - 1));
    return { [`${prefix}DateFrom`]: dateConverter(start), [`${prefix}DateTo`]: dateConverter(end) };
  }
  return { [`${prefix}DateFrom`]: null, [`${prefix}DateTo`]: null };
};

/** 체크 해제 시 query 에서 이 필터의 키를 전부 비운다 */
export const clearCountFilter = prefix => ({
  [`${prefix}Mode`]: null,
  [`${prefix}Year`]: null,
  [`${prefix}Month`]: null,
  [`${prefix}RecentDays`]: null,
  [`${prefix}Count`]: null,
  [`${prefix}CountCondition`]: null,
  [`${prefix}DateFrom`]: null,
  [`${prefix}DateTo`]: null,
});

/** 체크 시 기본값. 전체기간 · 0회 이하 */
export const defaultCountFilter = (prefix, defaultCondition = 'LTE') => {
  const now = new Date();
  return {
    [`${prefix}Mode`]: 'ALL',
    [`${prefix}Year`]: String(now.getFullYear()),
    [`${prefix}Month`]: String(now.getMonth() + 1),
    [`${prefix}RecentDays`]: String(COUNT_RECENT_PRESETS[0]),
    [`${prefix}Count`]: '0',
    [`${prefix}CountCondition`]: defaultCondition,
  };
};

const CountPeriodFilter = ({ prefix, title, hint, enabled, onToggle, query, setQuery, defaultCondition = 'LTE' }) => {
  const { t } = useTranslation();
  const k = name => `${prefix}${name}`;
  const nowYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: YEAR_RANGE }, (_, i) => nowYear - i);

  // 모드/입력값을 query 에 반영하면서 DateFrom/To 를 함께 파생시킨다
  const patch = changes => {
    const next = { ...query, ...changes };
    setQuery({
      ...next,
      ...computeDates(prefix, next[k('Mode')], next[k('Year')], next[k('Month')], next[k('RecentDays')]),
    });
  };

  const handleToggle = checked => {
    onToggle(checked);
    if (checked) patch(defaultCountFilter(prefix, defaultCondition));
    else setQuery({ ...query, ...clearCountFilter(prefix) });
  };

  return (
    <FilterSectionFlex>
      <SectionTitleWithCheckbox>
        <SmallCheckbox checked={enabled} onChange={e => handleToggle(e.target.checked)} />
        <SectionTitleText>{t(title)}</SectionTitleText>
        {hint && (
          <Tooltip title={hint} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: '#9aa0a6', cursor: 'help' }} />
          </Tooltip>
        )}
      </SectionTitleWithCheckbox>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* 윗줄: 라디오 + 횟수 + 이하/이상. 아랫줄: 모드별 기간 입력만 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <RadioContainer>
            {COUNT_MODE_OPTIONS.map(option => (
              <RadioItem key={option.value}>
                <StyledRadio
                  checked={query[k('Mode')] === option.value}
                  disabled={!enabled}
                  onChange={() => patch({ [k('Mode')]: option.value })}
                />
                <CommonText variant="label">{t(option.label)}</CommonText>
              </RadioItem>
            ))}
          </RadioContainer>
          <CustomInputWrapper>
            <CustomInput
              size="small"
              value={query[k('Count')] ?? ''}
              onChange={e => patch({ [k('Count')]: normalizeCountInput(e.target.value) })}
              onFocus={e => e.target.select()}
              disabled={!enabled}
            />
            <CustomInputSuffix>{t('회')}</CustomInputSuffix>
          </CustomInputWrapper>
          <StyledSelect
            value={query[k('CountCondition')] || defaultCondition}
            onChange={e => patch({ [k('CountCondition')]: e.target.value })}
            disabled={!enabled}
            sx={{ width: '84px' }}>
            {COUNT_CONDITION_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </StyledSelect>
        </div>
        {query[k('Mode')] !== 'ALL' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {query[k('Mode')] === 'MONTH' && (
              <>
                <StyledSelect value={query[k('Year')] || ''} onChange={e => patch({ [k('Year')]: e.target.value })} sx={{ width: '96px' }}>
                  {yearOptions.map(year => (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                      {t('년')}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <StyledSelect value={query[k('Month')] || ''} onChange={e => patch({ [k('Month')]: e.target.value })} sx={{ width: '84px' }}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <MenuItem key={month} value={String(month)}>
                      {month}
                      {t('월')}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </>
            )}
            {query[k('Mode')] === 'RECENT' && (
              <>
                {COUNT_RECENT_PRESETS.map(days => (
                  <FilterChip
                    key={days}
                    isActive={String(query[k('RecentDays')]) === String(days)}
                    onClick={() => enabled && patch({ [k('RecentDays')]: String(days) })}>
                    {days}
                    {t('일')}
                  </FilterChip>
                ))}
                <CustomInputWrapper>
                  <CustomInput
                    size="small"
                    value={query[k('RecentDays')] || ''}
                    onChange={e => patch({ [k('RecentDays')]: normalizeCountInput(e.target.value) })}
                    onFocus={e => e.target.select()}
                    disabled={!enabled}
                  />
                  <CustomInputSuffix>{t('일 이내')}</CustomInputSuffix>
                </CustomInputWrapper>
              </>
            )}
          </div>
        )}
      </div>
    </FilterSectionFlex>
  );
};

export default CountPeriodFilter;
